/* ══════════════════════════════════════════════════════════════════════
   useConsoleData — maps live Firestore + console backend into the exact
   ConsoleData shape the design consumes. This is the single wiring layer:
   the view (TrasseConsole) never touches Firestore. Everything is best-effort
   with graceful fallbacks so a sparse/empty tenant still renders the design.
   ══════════════════════════════════════════════════════════════════════ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../firebase';
import { bill, campusOf, PlanId } from '../components/subscriptionModel';
import { isLorryOrg, isFamilyOrg } from '../fleet/fleetConstants';
import { fetchConsoleAdmins } from '../services/consoleApi';
import {
  ConsoleData, ICON, Org, OrgStatus, Admin, AdminStatus, Bus, BusGroup, AdminGroup,
  Lorry, CalEvent, ContentItem, FuelSchool, DriverRating, RevLost, DistancePair, Notif, Kpi,
  AddonConn, AddonKey, EstimatedPriceOrg, PlatformAlert,
} from './consoleModel';

/* ── generic doc bag ── */
type Doc = Record<string, any> & { id: string };
const snapDocs = (snap: any): Doc[] => snap.docs.map((d: any) => ({ id: d.id, ...d.data() }));

/* ── time helpers ── */
function toMillis(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts === 'number') return ts;
  if (ts instanceof Date) return ts.getTime();
  if (typeof ts === 'string') { const t = Date.parse(ts); return isNaN(t) ? null : t; }
  if (typeof ts === 'object' && typeof ts.seconds === 'number') return ts.seconds * 1000;
  if (typeof ts?.toMillis === 'function') { try { return ts.toMillis(); } catch { return null; } }
  return null;
}
const timeAgo = (ms: number | null): string => {
  if (!ms) return '';
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

/* ── currency ── */
const kesShort = (n: number): string => {
  if (n >= 1e6) return `KES ${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `KES ${Math.round(n / 1e3)}K`;
  return `KES ${Math.round(n)}`;
};
const kShort = (n: number): string => (n >= 1e3 ? `${Math.round(n / 1e3)}K` : String(Math.round(n)));
const nfmt = (n: number): string => n.toLocaleString('en-KE');

/* ── org status derivation (mirrors Dashboard cascade) ── */
function orgIsActive(o: Doc): boolean {
  const status = String(o.subscriptionStatus || '').toLowerCase();
  if (!o.isActive) return false;
  if (status && status !== 'active') return false;
  const exp = toMillis(o.expirationDate);
  if (exp != null && exp < Date.now()) return false;
  return true;
}
/** True when the org doc carries a valid {lat,lng} home coordinate. */
function hasHomeLocation(o: Doc): boolean {
  const h = o.homeLocations;
  return !!(h && typeof h === 'object' && typeof h.lat === 'number' && typeof h.lng === 'number');
}
function deriveOrgStatus(o: Doc): OrgStatus {
  const status = String(o.subscriptionStatus || '').toLowerCase();
  if (o.pendingActivation) return 'Pending';        // invited, admin hasn't signed in yet
  /* A SCHOOL THAT HAS ASKED TO LEAVE OUTRANKS EVERY COSMETIC STATE BELOW.
   *
   * Gabriel cancelled Uchiha Academy on 2026-08-13 and the console went on
   * showing "Pending" — because the legacy-org backfill rule further down
   * (otherwise-active org with no home pin reads Pending) was reached first.
   * The one screen PEAR watches to spot churn said nothing at all, and the org
   * card still read "Renews 01 Sep 2026" for a subscription that CLOSES that
   * day. Missing a cancellation is missing the only chance to save the account. */
  if (o.cancelAtPeriodEnd === true && status !== 'canceled') return 'Canceling';
  if (status === 'suspended') return 'Suspended';
  if ((o.paymentFailures || 0) > 0 && orgIsActive(o)) return 'Suspended';
  if (o.isPilot) return 'Trial';
  // legacy-org backfill: an otherwise-active org with no home location reads
  // Pending until it is set — but suspended/expired/inactive (handled above /
  // below) still win, so a dead org is never relabeled Pending.
  if (orgIsActive(o) && !hasHomeLocation(o)) return 'Pending';
  if (orgIsActive(o)) return 'Active';
  return 'Expired';
}
/* ── fuel prices: who is running on an estimate ──
 *
 * The twin of classifyOrgPrices() in the backend's engine/fuel/opsPriceAlert.js.
 * The backend emails PEAR this same list, so if these two ever disagree the
 * console will contradict the email that sent someone to look at it. Keep them
 * in step: same field names, same order of tests, same four kinds. */

const currentPriceMonth = (): string => {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

/** Mirrors getsEpraFill: premium/enterprise on a live org, pilots included. */
function promisedEpraCeiling(o: Doc): boolean {
  if (o.isActive === false || o.subscriptionStatus === 'expired') return false;
  const plan = String(o.subscriptionPlan || o.plan || '').trim().toLowerCase();
  return plan === 'premium' || plan === 'enterprise';
}

function classifyOrgPrices(o: Doc, priceDoc: Doc | null): Omit<EstimatedPriceOrg, 'code' | 'name' | 'plan'> | null {
  const source = priceDoc?.source ?? null;
  const stations = Array.isArray(priceDoc?.stations) ? priceDoc!.stations : [];
  if (source === 'admin' && stations.length > 0) return null;

  const promisedEpra = promisedEpraCeiling(o);
  if (!priceDoc) {
    return { kind: 'missing', promisedEpra, detail: 'No fuel prices on file for this month at all' };
  }
  if (source === 'epra') return null;
  if (!promisedEpra) {
    return {
      kind: 'basic_no_stations', promisedEpra,
      detail: 'No station prices entered — every cost figure is a regional estimate',
    };
  }
  if (source === 'carried_forward') {
    return {
      kind: 'carried_forward', promisedEpra,
      detail: `Still on ${priceDoc.carriedFrom || 'last month'}'s prices`
        + (priceDoc.epraDiagText ? ` — ${priceDoc.epraDiagText}` : ''),
    };
  }
  return {
    kind: 'regional_default', promisedEpra,
    detail: 'On a regional average instead of an EPRA ceiling'
      + (priceDoc.epraDiagText ? ` — ${priceDoc.epraDiagText}` : ''),
  };
}

/** Plan-mix bucket used by the donut + plan column. */
function planLabel(o: Doc): string {
  if (o.isPilot || o.pendingActivation) return 'Trial';
  const p = String(o.subscriptionPlan || '').toLowerCase();
  if (p === 'premium') return 'Premium';
  if (p === 'enterprise') return 'Enterprise';
  if (p === 'basic') return 'Standard';
  return 'Standard';
}
function planBucket(o: Doc): 'Premium' | 'Standard' | 'Trial' {
  const l = planLabel(o);
  if (l === 'Trial') return 'Trial';
  if (l === 'Premium' || l === 'Enterprise') return 'Premium';
  return 'Standard';
}

/** Monthly recurring revenue for an org, KES. 0 if not active/pilot. */
function orgMRR(o: Doc, size: number, campus = 1): number {
  if (!orgIsActive(o) || o.isPilot) return 0;
  const plan = (String(o.subscriptionPlan || 'premium').toLowerCase()) as PlanId;
  // A Trasse Families household is one flat monthly subscription — counting
  // children or vehicles would multiply a fixed price into a fictional MRR.
  // (HOUSEHOLD_PRICE is flat across every plan id, so the default above cannot
  // change what a household contributes.)
  if (isFamilyOrg(o.productType)) return bill(plan, 1, 'household', 'month');
  const unit = isLorryOrg(o.productType) ? 'vehicle' : 'child';
  const n = Math.max(1, size || 1);
  // An Enterprise school pays by its place in its group (campusOf).
  return bill(plan, n, unit, 'month', 0, campus);
}

/* ── org-membership key (the 5-field fallback chain) ── */
const userOrgCode = (u: Doc): string =>
  u.organizationCode || u.schoolCode || u.classCode || u.institution || u.associatedClassroomId || '';

const ADMIN_ROLES = ['fleet_admin', 'school_admin', 'admin', 'owner', 'super_admin', 'administrator'];
const isAdminUser = (u: Doc): boolean => {
  const r = String(u.role || '').toLowerCase();
  return ADMIN_ROLES.includes(r) || r.includes('admin');
};
const initialsOf = (name: string) => (name || '?').split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
const prettyRole = (r: string): string => {
  const map: Record<string, string> = { fleet_admin: 'Fleet admin', school_admin: 'School admin', admin: 'School admin', owner: 'Owner', super_admin: 'Owner' };
  return map[String(r || '').toLowerCase()] || (r ? r.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'Admin');
};

/* ── bus status mapping ── */
/* MOVEMENT COMES FROM THE ROUTE, NOT FROM THE VEHICLE.
   `currentRoute.status` is 'active' while a run is under way; the bus's OWN
   `status` is 'active' when the vehicle is simply in service. Falling back from
   one to the other put both through the same list, so every parked bus in the
   fleet read "En route" on the PEAR console — the platform view of who is
   actually moving was green for everyone (review, 09-06). */
function busStatusInfo(o: Doc): { status: string; c: string } {
  const route = String(o?.currentRoute?.status || '').toLowerCase();
  const own = String(o?.status || '').toLowerCase();
  if (['en_route', 'enroute', 'in_progress', 'active', 'started', 'running'].includes(route)) return { status: 'En route', c: 'var(--green)' };
  if (['boarding', 'pickup', 'loading'].includes(route)) return { status: 'Boarding', c: 'var(--info)' };
  if (['maintenance', 'suspended'].includes(own)) return { status: 'Maintenance', c: 'var(--amber)' };
  return { status: 'Idle', c: 'var(--amber)' };
}
function lorryStatusInfo(o: Doc): { status: string; c: string } {
  const raw = String(o?.currentRoute?.status || o?.status || '').toLowerCase();
  if (o.maintenanceMode || raw === 'maintenance') return { status: 'Maintenance', c: 'var(--amber)' };
  if (['en_route', 'enroute', 'in_progress', 'active', 'trip', 'started'].includes(raw)) return { status: 'On route', c: 'var(--green)' };
  if (['loading', 'boarding'].includes(raw)) return { status: 'Loading', c: 'var(--info)' };
  return { status: 'Idle', c: 'var(--amber)' };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtDate = (ms: number | null): string | undefined => {
  if (ms == null) return undefined;
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};
/** Map a stored OrgAddon → the drilldown's connection status. */
function deriveConn(a: any): AddonConn {
  if (!a || typeof a !== 'object') return { status: 'disconnected' };
  const provider = a.provider || undefined;
  if (a.status === 'connected' && a.verified) return { status: 'connected', provider, endpoint: a.apiUrl };
  if (provider || a.wanted || a.status === 'connected') return { status: 'error', provider, endpoint: a.apiUrl };
  return { status: 'disconnected', provider };
}
/** True when an org was provisioned from the console (premium, unbilled). */
const isBetaOrg = (o: any): boolean => !!(o.beta || o.isBeta || o.consoleProvisioned || o.notBilled || o.subscriptionExempt || String(o.billingType || o.billing || '').toLowerCase() === 'manual');

export interface UseConsoleDataResult extends ConsoleData {}

export function useConsoleData(currentUser: { displayName?: string | null; email?: string | null } | null): ConsoleData {
  const [orgs, setOrgs] = useState<Doc[]>([]);
  const [users, setUsers] = useState<Doc[]>([]);
  const [buses, setBuses] = useState<Doc[]>([]);
  const [lorries, setLorries] = useState<Doc[]>([]);
  const [vehicles, setVehicles] = useState<Doc[]>([]);
  const [ops, setOps] = useState<Doc[]>([]);
  const [events, setEvents] = useState<Doc[]>([]);
  const [posts, setPosts] = useState<Doc[]>([]);
  const [menu, setMenu] = useState<Doc[]>([]);
  const [alerts, setAlerts] = useState<Doc[]>([]);
  const [ratings, setRatings] = useState<Doc[]>([]);
  const [fuelPrices, setFuelPrices] = useState<Doc[]>([]);
  const [platformAlertsRaw, setPlatformAlertsRaw] = useState<Doc[]>([]);
  const [consoleAdmins, setConsoleAdmins] = useState<{ email: string; grandfather: boolean }[]>([]);
  const [orgsLoaded, setOrgsLoaded] = useState(false);

  useEffect(() => {
    const subs: Array<() => void> = [];
    const sub = (name: string, setter: (d: Doc[]) => void, q?: any) => {
      try {
        const ref = q || collection(db, name);
        subs.push(onSnapshot(ref, (snap: any) => setter(snapDocs(snap)), () => setter([])));
      } catch { setter([]); }
    };
    subs.push(onSnapshot(collection(db, 'organizations'), (snap: any) => { setOrgs(snapDocs(snap)); setOrgsLoaded(true); }, () => { setOrgs([]); setOrgsLoaded(true); }));
    sub('users', setUsers);
    sub('buses', setBuses);
    sub('lorries', setLorries);
    sub('fleetVehicles', setVehicles);
    sub('events', setEvents);
    sub('posts', setPosts);
    sub('menu', setMenu);
    sub('driverRatings', setRatings);
    try { sub('operationHistory', setOps, query(collection(db, 'operationHistory'), orderBy('timestamp', 'desc'), limit(40))); } catch { sub('operationHistory', setOps); }
    try { sub('fleetAlerts', setAlerts, query(collection(db, 'fleetAlerts'), orderBy('timestamp', 'desc'), limit(20))); } catch { sub('fleetAlerts', setAlerts); }
    // This month only: fuelPrices keeps one document per org per month forever,
    // so an unfiltered listener would grow without bound and re-download every
    // month we have ever billed for a question that is only about right now.
    try {
      sub('fuelPrices', setFuelPrices, query(collection(db, 'fuelPrices'), where('month', '==', currentPriceMonth())));
    } catch { sub('fuelPrices', setFuelPrices); }
    try {
      sub('platformAlerts', setPlatformAlertsRaw, query(collection(db, 'platformAlerts'), orderBy('createdAtMs', 'desc'), limit(20)));
    } catch { sub('platformAlerts', setPlatformAlertsRaw); }
    fetchConsoleAdmins().then(r => { if (r.ok) setConsoleAdmins(r.admins.map(a => ({ email: a.email, grandfather: a.grandfather }))); }).catch(() => {});
    return () => { subs.forEach(u => { try { u(); } catch {} }); };
  }, []);

  return useMemo<ConsoleData>(() => {
    const now = new Date();

    /* per-org user counts */
    const studentsByOrg = new Map<string, number>();
    const usersByOrg = new Map<string, Doc[]>();
    for (const u of users) {
      if (u.deleted === true) continue;
      const code = userOrgCode(u);
      if (!code) continue;
      const role = String(u.role || '').toLowerCase();
      if (role === 'student' || role === 'child') studentsByOrg.set(code, (studentsByOrg.get(code) || 0) + 1);
      if (Array.isArray(u.children)) studentsByOrg.set(code, (studentsByOrg.get(code) || 0) + u.children.length);
      const list = usersByOrg.get(code) || []; list.push(u); usersByOrg.set(code, list);
    }
    const busesByOrg = new Map<string, Doc[]>();
    for (const b of buses) {
      const code = b.schoolCode || b.orgCode || b.organizationCode || '';
      if (!code) continue;
      const list = busesByOrg.get(code) || []; list.push(b); busesByOrg.set(code, list);
    }
    const orgByCode = new Map<string, Doc>();
    orgs.forEach(o => orgByCode.set(o.code, o));
    const userById = new Map<string, Doc>();
    users.forEach(u => userById.set(u.id, u));

    /* ── organizations table ── */
    const orgRows: (Org & { _mrr: number; _o: Doc })[] = orgs
      .filter(o => o.code)
      .map(o => {
        const students = studentsByOrg.get(o.code) ?? (o.studentCount || o.plannedChildren || 0);
        const busCount = busesByOrg.get(o.code)?.length ?? (o.plannedBuses || 0);
        const mrr = orgMRR(o, isLorryOrg(o.productType) ? (busCount || students || 1) : students, campusOf(o.code, o, orgByCode.get(String(o.parentOrgCode || ''))));
        // a console-created (beta) org reads "Pending" until its admin claims the invite
        const hasAdmin = (usersByOrg.get(o.code) || []).some(isAdminUser);
        let status = deriveOrgStatus(o);
        if (isBetaOrg(o) && status === 'Active' && !hasAdmin) status = 'Pending';
        const addons = o.addons || {};
        const conns: Partial<Record<AddonKey, AddonConn>> = {
          gps: deriveConn(addons.gps), fuel: deriveConn(addons.fuel),
          fees: deriveConn(addons.fees), sms: deriveConn(addons.sms),
        };
        const homeLocations = hasHomeLocation(o) ? { lat: o.homeLocations.lat, lng: o.homeLocations.lng } : null;
        return {
          name: o.name || o.code, code: o.code, plan: planLabel(o), status,
          /* Three states, on purpose: true / false from the document, and null
             when the field is absent (older docs). The removal guard treats
             only an explicit false as "nothing to cancel"; null falls back to
             the status label, so a legacy org still billing is never deletable
             by omission (self-review, 2026-09-30). */
          hasActiveSubscription: typeof o.hasActiveSubscription === 'boolean' ? o.hasActiveSubscription : null,
          productType: o.productType || null,
          students: nfmt(students), buses: String(busCount), mrr: mrr > 0 ? kShort(mrr) : '0',
          end: fmtDate(toMillis(o.expirationDate)), canceling: o.cancelAtPeriodEnd === true,
          /* `canceling` stays TRUE for ever — finalizeCancellation never clears
             cancelAtPeriodEnd — so the date tile went on reading "Closes 01 Sep"
             in the future tense for a school that had already closed on the 1st.
             Gabriel, 2026-09-02: "it shows expired, but that part of closes is
             still telling me 1st September". The close has HAPPENED once the
             backend writes subscriptionStatus 'canceled', so that is what the
             label follows. */
          closed: String(o.subscriptionStatus || '').toLowerCase() === 'canceled',
          beta: isBetaOrg(o), conns,
          homeLocations, locationSetupPending: !homeLocations,
          zanaCapUsd: (() => {
            if (o.zanaCapUsd == null || o.zanaCapUsd === '') return null;
            const n = Number(o.zanaCapUsd);
            if (!Number.isFinite(n) || n < 0) return null;
            return Math.min(100, n);
          })(),
          _mrr: mrr, _o: o,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));

    /* ── KPIs ── */
    const activeOrgs = orgRows.filter(r => r.status === 'Active');
    const totalMRR = orgRows.reduce((s, r) => s + r._mrr, 0);
    const totalStudents = orgs.reduce((s, o) => s + (studentsByOrg.get(o.code) || 0), 0);
    const trialCount = orgRows.filter(r => r.status === 'Trial').length;
    const fleetOnRoad = buses.filter(b => busStatusInfo(b).status === 'En route').length
      + vehicles.filter(v => lorryStatusInfo(v).status === 'On route').length
      + lorries.filter(v => lorryStatusInfo(v).status === 'On route').length;

    const kpis: Kpi[] = [
      { label: 'Total revenue', value: kesShort(totalMRR), d: ICON.revenue, delta: totalMRR > 0 ? '▲ MRR' : '—', up: true, note: 'monthly' },
      { label: 'Active schools', value: String(activeOrgs.length), d: ICON.school, delta: trialCount ? `▲ ${trialCount} trial` : '', up: true, note: `${orgRows.length} total` },
      { label: 'Students tracked', value: nfmt(totalStudents), d: ICON.users, delta: '', up: true, note: 'enrolled' },
      { label: 'Fleet on road', value: String(fleetOnRoad), d: ICON.bus, delta: '', up: true, note: `${buses.length + lorries.length + vehicles.length} vehicles` },
    ];

    /* ── plan mix ── */
    const planMix = {
      premium: orgRows.filter(r => planBucket(r._o) === 'Premium').length,
      standard: orgRows.filter(r => planBucket(r._o) === 'Standard').length,
      trial: orgRows.filter(r => planBucket(r._o) === 'Trial').length,
      activeSchools: activeOrgs.length,
    };

    /* ── revenue trend: MRR of orgs existing by each of the last 12 months ── */
    const points: number[] = [];
    const monthsLabels: string[] = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0).getTime();
      monthsLabels.push(MONTHS[d.getMonth()]);
      let sum = 0;
      for (const r of orgRows) {
        const created = toMillis(r._o.createdAt);
        if (created == null || created <= monthEnd) sum += r._mrr;
      }
      points.push(sum);
    }
    const revUp = points[11] >= points[0];
    const revDelta = points[0] > 0 ? Math.round(((points[11] - points[0]) / points[0]) * 100) : 0;
    const revenue = {
      points: points.some(p => p > 0) ? points : orgRows.map((_, i) => i + 1),
      months: monthsLabels,
      total: kesShort(totalMRR).replace('KES ', ''),
      deltaLabel: totalMRR > 0 ? `${revUp ? '▲' : '▼'} ${Math.abs(revDelta)}% vs start` : 'No billing yet',
      up: revUp,
    };

    /* ── revenue by school (top 5 by MRR) ── */
    const topRev = [...orgRows].filter(r => r._mrr > 0).sort((a, b) => b._mrr - a._mrr).slice(0, 5);
    const maxRev = topRev[0]?._mrr || 1;
    const schoolRev = topRev.map(r => ({ name: r.name, amt: kesShort(r._mrr), pct: Math.max(6, Math.round((r._mrr / maxRev) * 100)) }));

    /* ── recent activity from operationHistory ── */
    const opColor: Record<string, string> = { create: 'var(--green)', update: 'var(--info)', delete: 'var(--red)' };
    const opIcon: Record<string, string> = { create: 'M20 6L9 17l-5-5', update: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z', delete: 'M18 6 6 18M6 6l12 12' };
    const activity = ops.slice(0, 5).map(op => {
      const type = String(op.type || op.action || 'update').toLowerCase();
      const what = op.contentType || op.entity || op.target || 'item';
      const title = op.description || op.message || `${type[0]?.toUpperCase()}${type.slice(1)} · ${what}`;
      return { title: String(title).slice(0, 60), time: timeAgo(toMillis(op.timestamp || op.createdAt)), amt: '', c: opColor[type] || 'var(--accent)', d: opIcon[type] || ICON.school };
    });

    /* ── users: admins grouped by school (+ console owners) ── */
    const adminGroups: AdminGroup[] = [];
    const consoleAdminList: Admin[] = [];
    if (currentUser) {
      consoleAdminList.push({ name: currentUser.displayName || (currentUser.email || 'Owner').split('@')[0], email: currentUser.email || '', phone: '', role: 'Owner', status: 'Active' });
    }
    consoleAdmins.forEach(a => {
      if (currentUser && a.email === currentUser.email) return;
      consoleAdminList.push({ name: a.email.split('@')[0], email: a.email, phone: '', role: a.grandfather ? 'Owner' : 'Console admin', status: 'Active' });
    });
    if (consoleAdminList.length) adminGroups.push({ school: 'Trasse Console', tag: 'Owner', admins: consoleAdminList });

    orgRows.forEach(r => {
      const admins = (usersByOrg.get(r.code) || []).filter(isAdminUser).map(u => ({
        name: u.displayName || (u.email || '').split('@')[0] || 'Admin',
        email: u.email || '', phone: u.phone || '',
        role: prettyRole(u.role), status: (u.pendingActivation ? 'Pending' : 'Active') as AdminStatus,
      }));
      if (admins.length) adminGroups.push({ school: r.name, tag: r.status === 'Trial' ? `${r.code} · Trial` : r.code, admins });
    });

    /* ── bus tracking grouped by school ── */
    const busGroups: BusGroup[] = [];
    busesByOrg.forEach((list, code) => {
      const org = orgByCode.get(code);
      const mapped: Bus[] = list.map(b => {
        const info = busStatusInfo(b);
        const driverId = Array.isArray(b.driverIds) ? b.driverIds[0] : (Array.isArray(b.drivers) ? b.drivers[0] : b.driverId);
        const driver = (driverId && userById.get(driverId)?.displayName) || (driverId ? 'Assigned' : 'Unassigned');
        const kids = Array.isArray(b.children) ? b.children.filter((c: any) => ['boarded', 'onboard', 'picked_up', 'present'].includes(String(c.status || '').toLowerCase())).length : 0;
        const routeType = b.currentRoute?.type || b.routeName || '';
        return {
          plate: b.numberPlate || b.plate || b.id, route: routeType ? `${routeType}` : (b.route || '—'),
          status: info.status, driver, kids: String(kids), rating: Number(b.driverRating || 0), c: info.c,
        };
      });
      busGroups.push({ school: org?.name || code, code, buses: mapped });
    });
    busGroups.sort((a, b) => a.school.localeCompare(b.school));

    /* ── lorries ── */
    const lorrySource = [...lorries, ...vehicles];
    const lorriesMapped: Lorry[] = lorrySource.map(v => {
      const info = lorryStatusInfo(v);
      const eff = Number(v.fuelEfficiencyKmPerL || 0);
      const km = Number(v.monthlyDistanceKm || v.totalDistanceKm || 0);
      return {
        plate: v.numberPlate || v.plate || v.id, model: v.vehicleType || v.nickname || v.model || 'Vehicle',
        cargo: v.currentCargo || v.cargoType || '—', status: info.status,
        fuel: eff ? `${eff.toFixed(1)} km/L` : '—', dist: `${Math.round(km)} km`, c: info.c,
      };
    });
    const onRoad = lorrySource.filter(v => lorryStatusInfo(v).status === 'On route').length;
    const effs = lorrySource.map(v => Number(v.fuelEfficiencyKmPerL || 0)).filter(n => n > 0);
    const avgEff = effs.length ? (effs.reduce((a, b) => a + b, 0) / effs.length) : 0;
    const distToday = lorrySource.reduce((s, v) => s + Number(v.monthlyDistanceKm || 0), 0) / 30;
    const lorryMiniStats = [
      { k: 'Vehicles', v: String(lorrySource.length) },
      { k: 'On road', v: String(onRoad) },
      { k: 'Avg fuel', v: avgEff ? `${avgEff.toFixed(1)} km/L` : '—' },
      { k: 'Distance today', v: `${nfmt(Math.round(distToday))} km` },
    ];

    /* ── calendar (current month) ── */
    const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const firstDow = new Date(now.getFullYear(), now.getMonth(), 1).getDay(); // 0=Sun
    const firstWeekdayOffset = (firstDow + 6) % 7; // Monday-first
    const evColors: Record<number, string> = {};
    const palette = ['var(--accent)', 'var(--info)', 'var(--green)', 'var(--amber)'];
    const upcoming: { ms: number; ev: CalEvent }[] = [];
    events.forEach((e, i) => {
      const ms = toMillis(e.date || e.eventDate || e.startDate || e.timestamp);
      if (ms == null) return;
      const d = new Date(ms);
      const c = palette[i % palette.length];
      if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) evColors[d.getDate()] = c;
      if (ms >= now.getTime() - 86400000) upcoming.push({ ms, ev: { mon: MONTHS[d.getMonth()], day: String(d.getDate()), title: e.title || e.name || 'Event', meta: (e.organizationCode || e.schoolCode || 'All schools') + (e.type ? ` · ${e.type}` : ''), c } });
    });
    // renewals from org expiry
    orgRows.forEach(r => {
      const ms = toMillis(r._o.expirationDate);
      if (ms == null) return;
      const d = new Date(ms);
      if (d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth()) evColors[d.getDate()] = 'var(--amber)';
      if (ms >= now.getTime() && ms < now.getTime() + 45 * 86400000) upcoming.push({ ms, ev: { mon: MONTHS[d.getMonth()], day: String(d.getDate()), title: `${r.name} renewal`, meta: `${r.plan} · billing`, c: 'var(--amber)' } });
    });
    upcoming.sort((a, b) => a.ms - b.ms);
    const calendar = {
      monthLabel: `${['January','February','March','April','May','June','July','August','September','October','November','December'][now.getMonth()]} ${now.getFullYear()}`,
      daysInMonth, firstWeekdayOffset, today: now.getDate(), eventColors: evColors,
      events: upcoming.slice(0, 6).map(u => u.ev),
    };

    /* ── content (posts + menu) ── */
    const contentItems: ContentItem[] = [
      ...posts.slice(0, 6).map(p => ({ title: p.title || p.text?.slice(0, 40) || 'Post', meta: `${p.organizationCode || p.schoolCode || 'Platform'} · ${timeAgo(toMillis(p.createdAt))}`, ph: p.imageUrl ? 'image' : 'announcement' })),
      ...menu.slice(0, 3).map(m => ({ title: m.title || m.name || 'Menu', meta: `${m.organizationCode || m.schoolCode || 'School'} · menu`, ph: 'menu image' })),
    ].slice(0, 6);

    /* ── analytics ── */
    // driver ratings per org
    const ratingByOrg = new Map<string, number[]>();
    ratings.forEach(rt => {
      const code = rt.orgCode || rt.schoolCode || '';
      const stars = Number(rt.stars ?? rt.rating ?? 0);
      if (!code || !stars) return;
      const list = ratingByOrg.get(code) || []; list.push(stars); ratingByOrg.set(code, list);
    });
    const driverRatings: DriverRating[] = orgRows
      .map(r => {
        const list = ratingByOrg.get(r.code) || [];
        const avg = list.length ? list.reduce((a, b) => a + b, 0) / list.length : 0;
        return { name: r.name, v: avg, drivers: `${list.length} driver${list.length === 1 ? '' : 's'}` };
      })
      .filter(d => d.v > 0).sort((a, b) => b.v - a.v).slice(0, 5);

    // fuel efficiency per school from buses benchmark
    const fuelByOrg = new Map<string, number[]>();
    buses.forEach(b => {
      const code = b.schoolCode || b.orgCode || '';
      const eff = Number(b.benchmarkKmPerL || b.fuelEfficiencyKmPerL || 0);
      if (!code || !eff) return;
      const list = fuelByOrg.get(code) || []; list.push(eff); fuelByOrg.set(code, list);
    });
    const fuelRows = orgRows.map(r => {
      const list = fuelByOrg.get(r.code) || [];
      const avg = list.length ? list.reduce((a, b) => a + b, 0) / list.length : 0;
      return { name: r.name, v: avg };
    }).filter(f => f.v > 0).sort((a, b) => b.v - a.v).slice(0, 5);
    const maxFuel = fuelRows[0]?.v || 1;
    const fuelPerSchool: FuelSchool[] = fuelRows.map(f => ({ name: f.name, v: `${f.v.toFixed(1)} km/L`, pct: Math.max(10, Math.round((f.v / maxFuel) * 100)), good: f.v >= 7 }));

    // revenue lost — expired / suspended orgs with their would-be MRR
    const lost = orgRows
      .filter(r => r.status === 'Expired' || r.status === 'Suspended')
      .map(r => {
        const students = studentsByOrg.get(r.code) || r._o.studentCount || r._o.plannedChildren || 1;
        const would = orgMRR({ ...r._o, isActive: true, isPilot: false, subscriptionStatus: 'active' }, isLorryOrg(r._o.productType) ? (busesByOrg.get(r.code)?.length || students) : students, campusOf(r.code, r._o, orgByCode.get(String(r._o.parentOrgCode || ''))));
        return { name: r.name, amt: would };
      })
      .filter(x => x.amt > 0).sort((a, b) => b.amt - a.amt).slice(0, 4);
    const lostTotal = lost.reduce((s, x) => s + x.amt, 0);
    const maxLost = lost[0]?.amt || 1;
    const revLost: RevLost[] = lost.map(x => ({ name: x.name, v: kesShort(x.amt), pct: Math.max(10, Math.round((x.amt / maxLost) * 100)) }));

    // distance pairs — this month per top orgs (last approximated from history if present)
    const distByOrg = orgRows.map(r => {
      const bs = busesByOrg.get(r.code) || [];
      const thisM = bs.reduce((s, b) => s + Number(b.monthlyDistanceKm || 0), 0);
      const total = bs.reduce((s, b) => s + Number(b.totalDistanceKm || 0), 0);
      const last = Math.max(0, total - thisM) > 0 ? (total - thisM) : thisM * 0.9;
      return { thisM, last };
    }).filter(d => d.thisM > 0).slice(0, 5);
    const maxDist = Math.max(1, ...distByOrg.map(d => Math.max(d.thisM, d.last)));
    const distancePairs: DistancePair[] = distByOrg.map(d => ({ thisM: d.thisM / maxDist, last: d.last / maxDist }));

    const totalKm = buses.reduce((s, b) => s + Number(b.monthlyDistanceKm || 0), 0) + lorrySource.reduce((s, v) => s + Number(v.monthlyDistanceKm || 0), 0);
    const allRatings = Array.from(ratingByOrg.values()).flat();
    const avgRating = allRatings.length ? allRatings.reduce((a, b) => a + b, 0) / allRatings.length : 0;
    const allFuel = Array.from(fuelByOrg.values()).flat();
    const avgFuelAll = allFuel.length ? allFuel.reduce((a, b) => a + b, 0) / allFuel.length : avgEff;

    const anaKpis: Kpi[] = [
      { label: 'Avg fuel efficiency', value: avgFuelAll ? `${avgFuelAll.toFixed(1)} km/L` : '—', d: ICON.fuel, delta: '', up: true, note: 'fleet-wide' },
      { label: 'Avg distance / day', value: `${nfmt(Math.round(totalKm / 30))} km`, d: ICON.distance, delta: '', up: true, note: 'all vehicles' },
      { label: 'Avg driver rating', value: avgRating ? `${avgRating.toFixed(1)} / 5` : '—', d: ICON.star, delta: '', up: true, note: `${allRatings.length} rated` },
      { label: 'Revenue lost (30d)', value: kesShort(lostTotal), d: ICON.revenue, delta: '', up: false, note: 'churn + failed' },
    ];

    /* ── schools costing fuel off an estimate this month ── */
    const priceByOrg = new Map<string, Doc>();
    fuelPrices.forEach(p => { if (p.orgCode) priceByOrg.set(p.orgCode, p); });
    const estimatedPriceOrgs: EstimatedPriceOrg[] = orgs
      .filter(o => o.code && o.isActive !== false)
      .map(o => {
        const problem = classifyOrgPrices(o, priceByOrg.get(o.code) || null);
        if (!problem) return null;
        return {
          code: o.code,
          name: (o.name || o.organizationName || o.code) as string,
          plan: planLabel(o),
          ...problem,
        };
      })
      .filter((r): r is EstimatedPriceOrg => r !== null)
      // A broken EPRA promise is ours to fix and outranks a Basic school that
      // simply has not typed its stations.
      .sort((a, b) => Number(b.promisedEpra) - Number(a.promisedEpra) || a.name.localeCompare(b.name));

    /* ── PEAR-ops critical alerts (press fuel update, etc.) ── */
    const platformAlerts: PlatformAlert[] = platformAlertsRaw
      .filter(a => a.acknowledged !== true)
      .map(a => ({
        id: a.id,
        kind: String(a.kind || ''),
        severity: String(a.severity || 'critical'),
        title: String(a.title || 'Alert'),
        message: String(a.message || ''),
        caps: a.caps || null,
        sources: Array.isArray(a.sources) ? a.sources : [],
        acknowledged: false,
        createdAtMs: typeof a.createdAtMs === 'number' ? a.createdAtMs : toMillis(a.createdAt),
      }));

    /* ── notifications from alerts + expiring trials ── */
    const notifs: Notif[] = [];
    // Critical PEAR-ops items first — same weight as an incident.
    platformAlerts.slice(0, 2).forEach(a => {
      notifs.push({
        title: a.title,
        time: timeAgo(a.createdAtMs ?? null) || 'now',
        c: 'var(--red)',
        d: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
        // Platform-ops alerts live on the dashboard's ops card, where they can
        // be acknowledged.
        tab: 'dashboard',
      });
    });
    // Money first among schools: costs and overspend detection on a guess.
    const promisedButEstimated = estimatedPriceOrgs.filter(r => r.promisedEpra);
    if (promisedButEstimated.length) {
      notifs.push({
        title: promisedButEstimated.length === 1
          ? `${promisedButEstimated[0].name} is on estimated fuel prices`
          : `${promisedButEstimated.length} Premium schools are on estimated fuel prices`,
        time: 'this month',
        c: 'var(--red)',
        d: 'M14 3v6l4 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2zM4 9h10',
        // One school → straight to it; several → the dashboard card that
        // lists them all.
        ...(promisedButEstimated.length === 1
          ? { orgCode: promisedButEstimated[0].code }
          : { tab: 'dashboard' }),
      });
    }
    alerts.slice(0, 3).forEach(a => notifs.push({ title: a.message || a.title || 'Fleet alert', time: timeAgo(toMillis(a.timestamp)), c: 'var(--amber)', d: 'M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z', orgCode: (a.schoolCode || a.orgCode || a.organizationCode || '') || undefined }));
    orgRows.filter(r => r.status === 'Trial').slice(0, 3 - notifs.length).forEach(r => {
      if (notifs.length >= 3) return;
      notifs.push({ title: `${r.name} is on a trial`, time: 'now', c: 'var(--info)', d: 'M12 16v-4M12 8h.01', orgCode: r.code });
    });

    return {
      loading: !orgsLoaded,
      user: { name: currentUser?.displayName || (currentUser?.email || 'Owner').split('@')[0], role: 'Owner', initials: initialsOf(currentUser?.displayName || currentUser?.email || 'Owner') },
      kpis, revenue, planMix, schoolRev, activity,
      orgs: orgRows.map(({ _mrr, _o, ...rest }) => rest),
      adminGroups, busGroups,
      lorries: lorriesMapped, lorryMiniStats,
      calendar, contentItems,
      anaKpis, fuelPerSchool, driverRatings, revLost,
      distancePairs, distanceTotalLabel: 'Total fleet kilometres', revLostTotal: `${kesShort(lostTotal)} total`,
      notifs, estimatedPriceOrgs, platformAlerts,
    };
  }, [orgs, users, buses, lorries, vehicles, ops, events, posts, menu, alerts, ratings, fuelPrices, platformAlertsRaw, consoleAdmins, orgsLoaded, currentUser]);
}
